<template>
  <section ref="rootRef" data-testid="insights-perspective-market">
    <div class="grid grid-cols-1 gap-6 tablet:grid-cols-2" data-testid="insights-market-charts">
      <InsightsMetricCard
        class="flex flex-col [&>div]:flex [&>div]:grow [&>div]:flex-col"
        :headline="t('Reviews.product.insights.market.ranking.title')"
        :subheadline="t('Reviews.product.insights.market.ranking.subtitle')"
        :tooltip="t('Reviews.product.insights.market.ranking.tooltip')"
        data-testid="insights-market-card-ranking"
      >
        <InsightsDataState :loading="loading" :state="states.ranking" data-testid="insights-market-ranking">
          <InsightsHeroMetric
            :value="rankingHero.value"
            :trend="rankingHero.trend"
            :caption="rankingHero.caption"
            data-testid="insights-market-ranking-hero"
          />
          <InsightsRankLineChart
            v-if="ready && tokens"
            class="mt-4 grow"
            :categories="ranking.weeks"
            :values="ranking.ranks"
            :color="tokens.palette[0]"
            :locale="locale"
            data-testid="insights-market-ranking-chart"
            @hover="hoveredWeek = $event"
          />
          <p v-if="ranking.dataSince" class="mt-4 txt-body-s-regular text-subtle" data-testid="insights-market-ranking-data-since">
            {{ t('Reviews.product.insights.market.ranking.dataSince', { date: ranking.dataSince }) }}
          </p>
        </InsightsDataState>
      </InsightsMetricCard>

      <InsightsMetricCard
        class="flex flex-col [&>footer]:mt-auto"
        :headline="t('Reviews.product.insights.market.topTen.title')"
        :subheadline="t('Reviews.product.insights.market.topTen.subtitle')"
        :tooltip="t('Reviews.product.insights.market.topTen.tooltip')"
        data-testid="insights-market-card-top-ten"
      >
        <InsightsDataState :loading="topTenLoading" :state="topTenState" skeleton="compact" data-testid="insights-market-top-ten">
          <ol class="-mx-2 grid grid-cols-[auto_auto_minmax(0,1fr)_auto_auto] gap-x-3">
            <li
              v-for="tool in visibleTopTen"
              :key="tool.slug"
              class="col-span-full grid grid-cols-subgrid items-center rounded-md p-2"
              :class="tool.slug === product.slug ? 'bg-primary-subtle text-primary' : 'text-main'"
              :aria-current="tool.slug === product.slug ? 'true' : undefined"
              data-testid="insights-market-top-ten-row"
            >
              <span class="text-right txt-label-m tabular-nums" data-testid="insights-market-top-ten-rank">{{ tool.rank }}</span>
              <ProductLogo :sources="{ 1: tool.logoUrl ?? PLACEHOLDER_LOGO }" size="small" />
              <span class="truncate txt-body-m-regular" data-testid="insights-market-top-ten-name">{{ tool.name }}</span>
              <span class="text-right txt-body-s-regular tabular-nums" data-testid="insights-market-top-ten-reviews">
                {{ t('Reviews.product.insights.market.topTen.reviewCount', { count: tool.reviewCount }) }}
              </span>
              <span class="inline-flex items-center gap-1 txt-body-s-regular tabular-nums" data-testid="insights-market-top-ten-rating">
                <StarIcon class="lucide-icon-s text-icon-secondary" fill="currentColor" aria-hidden="true" />
                {{ tool.rating }}
              </span>
            </li>
          </ol>
        </InsightsDataState>
        <template v-if="showTopTenPagination" #footer>
          <div class="flex justify-end gap-1" data-testid="insights-market-top-ten-pagination">
            <IconButton
              size="extra-small"
              variant="ghost-neutral"
              :icon="ChevronLeftIcon"
              :disabled="topTenPage === 0"
              :aria-label="t('Reviews.product.insights.market.topTen.previousPage')"
              data-testid="insights-market-top-ten-previous"
              @click="topTenPage--"
            />
            <IconButton
              size="extra-small"
              variant="ghost-neutral"
              :icon="ChevronRightIcon"
              :disabled="topTenPage === topTenPageCount - 1"
              :aria-label="t('Reviews.product.insights.market.topTen.nextPage')"
              data-testid="insights-market-top-ten-next"
              @click="topTenPage++"
            />
          </div>
        </template>
      </InsightsMetricCard>

      <InsightsMetricCard
        class="tablet:col-span-2"
        :headline="t('Reviews.product.insights.market.demandSupply.demand')"
        :subheadline="t('Reviews.product.insights.market.demandSupply.demandSubtitle')"
        :tooltip="t('Reviews.product.insights.market.demandSupply.demandTooltip')"
        data-testid="insights-market-card-demand"
      >
        <InsightsDataState :loading="loading" :state="states.demand" data-testid="insights-market-demand">
          <div class="flex items-start gap-12">
            <InsightsHeroMetric
              :value="heroes.demand.value"
              :trend="heroes.demand.trend"
              :caption="windowCaption('demandSupply.demandWindowCaption', heroes.demand.caption)"
              data-testid="insights-market-demand-hero"
            />
            <InsightsHeroMetric
              size="m"
              :value="heroes.density.value"
              :trend="heroes.density.trend"
              :caption="t('Reviews.product.insights.market.demandSupply.densityCaption', { month: heroes.density.month })"
              data-testid="insights-market-density-hero"
            >
              <template #icon>
                <CircleSlash2Icon class="lucide-icon-m" />
              </template>
            </InsightsHeroMetric>
          </div>
          <InsightsBarChart
            v-if="ready && tokens"
            class="mt-4"
            :categories="charts.months"
            :values="charts.demand"
            :color="tokens.palette[0]"
            :locale="locale"
            data-testid="insights-market-demand-chart"
          />
        </InsightsDataState>
      </InsightsMetricCard>

      <InsightsMetricCard
        :headline="t('Reviews.product.insights.market.demandSupply.supply')"
        :subheadline="t('Reviews.product.insights.market.demandSupply.supplySubtitle')"
        :tooltip="t('Reviews.product.insights.market.demandSupply.supplyTooltip')"
        data-testid="insights-market-card-supply"
      >
        <InsightsDataState :loading="loading" :state="states.demand" data-testid="insights-market-supply">
          <InsightsHeroMetric
            :value="heroes.supply.value"
            :trend="heroes.supply.trend"
            :caption="t('Reviews.product.insights.market.demandSupply.supplyCaption', { month: heroes.supply.month })"
            data-testid="insights-market-supply-hero"
          />
          <InsightsBarChart
            v-if="ready && tokens"
            class="mt-4"
            :categories="charts.months"
            :values="charts.supply"
            :color="tokens.palette[2]"
            :locale="locale"
            data-testid="insights-market-supply-chart"
          />
        </InsightsDataState>
      </InsightsMetricCard>

      <InsightsMetricCard
        class="flex flex-col [&>footer]:mt-auto"
        :headline="t('Reviews.product.insights.market.newEntrants.title')"
        :subheadline="t('Reviews.product.insights.market.newEntrants.subtitle')"
        data-testid="insights-market-new-entrants"
      >
        <InsightsDataState
          :loading="loading || productsLoading"
          :state="states.newEntrants"
          skeleton="compact"
          data-testid="insights-market-new-entrants-data"
        >
          <ul class="grid grid-cols-[auto_minmax(0,1fr)_auto_auto_auto] gap-x-3">
            <li
              v-for="tool in visibleEntrants"
              :key="tool.slug"
              class="col-span-full grid grid-cols-subgrid items-center py-2"
              data-testid="insights-market-new-entrant"
            >
              <ProductLogo :sources="{ 1: tool.logoUrl ?? PLACEHOLDER_LOGO }" size="small" />
              <span class="min-w-0 truncate txt-body-m-regular text-main">{{ tool.name }}</span>
              <span
                v-if="tool.reviewCount !== null"
                class="text-right txt-body-s-regular whitespace-nowrap tabular-nums"
                data-testid="insights-market-new-entrant-reviews"
              >
                {{ t('Reviews.product.insights.market.newEntrants.reviewCount', { count: tool.reviewCount }, tool.reviewCountValue ?? 0) }}
              </span>
              <span v-else />
              <span
                v-if="tool.rating !== null"
                class="inline-flex items-center gap-1 txt-body-s-regular tabular-nums"
                data-testid="insights-market-new-entrant-rating"
              >
                <StarIcon class="lucide-icon-s text-icon-secondary" fill="currentColor" aria-hidden="true" />
                {{ tool.rating }}
              </span>
              <span
                v-else-if="tool.reviewCount !== null"
                class="inline-flex items-center gap-1 txt-body-s-regular text-subtle"
                data-testid="insights-market-new-entrant-unrated"
              >
                <StarIcon class="lucide-icon-s text-icon-subtlest" aria-hidden="true" />
                —
              </span>
              <span v-else />
              <a
                class="state-layer-neutral inline-flex size-8 shrink-0 items-center justify-center rounded-md text-main omr-focus-visible-outline"
                :href="profileUrl(tool.slug)"
                target="_blank"
                rel="noopener"
                :aria-label="t('Reviews.product.insights.market.newEntrants.openProfile', { tool: tool.name })"
                data-testid="insights-market-new-entrant-profile"
              >
                <ExternalLinkIcon class="lucide-icon-s" aria-hidden="true" />
              </a>
            </li>
            <li
              v-if="showHiddenEntrants"
              class="col-span-full flex h-12 items-center txt-body-m-regular text-subtle"
              data-testid="insights-market-new-entrants-more"
            >
              {{ t('Reviews.product.insights.market.newEntrants.more', { count: hiddenNewEntrants }) }}
            </li>
          </ul>
        </InsightsDataState>
        <template v-if="showEntrantsPagination" #footer>
          <div class="flex justify-end gap-1" data-testid="insights-market-new-entrants-pagination">
            <IconButton
              size="extra-small"
              variant="ghost-neutral"
              :icon="ChevronLeftIcon"
              :disabled="entrantsPage === 0"
              :aria-label="t('Reviews.product.insights.market.newEntrants.previousPage')"
              data-testid="insights-market-new-entrants-previous"
              @click="entrantsPage--"
            />
            <IconButton
              size="extra-small"
              variant="ghost-neutral"
              :icon="ChevronRightIcon"
              :disabled="entrantsPage === pageCount - 1"
              :aria-label="t('Reviews.product.insights.market.newEntrants.nextPage')"
              data-testid="insights-market-new-entrants-next"
              @click="entrantsPage++"
            />
          </div>
        </template>
      </InsightsMetricCard>

      <InsightsMetricCard
        class="tablet:col-span-2"
        :headline="t('Reviews.product.insights.market.reviewGrowth.title')"
        :subheadline="t('Reviews.product.insights.market.reviewGrowth.subtitle')"
        :tooltip="t('Reviews.product.insights.market.reviewGrowth.info')"
        data-testid="insights-market-card-review-growth"
      >
        <InsightsDataState :loading="loading" :state="states.reviewGrowth" data-testid="insights-market-review-growth">
          <InsightsHeroMetric
            :value="heroes.reviews.value"
            :trend="heroes.reviews.trend"
            :caption="windowCaption('reviewGrowth.windowCaption', heroes.reviews.caption)"
            data-testid="insights-market-review-growth-hero"
          />
          <InsightsReviewGrowthChart
            v-if="ready && tokens"
            class="mt-4"
            :categories="charts.weeks"
            :bar-values="charts.newPerWeek"
            :line-values="charts.cumulative"
            :bar-color="tokens.palette[3]"
            :line-color="tokens.palette[0]"
            :bar-label="t('Reviews.product.insights.market.reviewGrowth.legendNew')"
            :line-label="t('Reviews.product.insights.market.reviewGrowth.legendCumulative')"
            :locale="locale"
            data-testid="insights-market-review-growth-chart"
          />
        </InsightsDataState>
      </InsightsMetricCard>
    </div>
  </section>
</template>

<script setup lang="ts">
import { IconButton } from '@ramp106/omrjs-core-ui'
import { ChevronLeftIcon, ChevronRightIcon, CircleSlash2Icon, ExternalLinkIcon, StarIcon } from '@ramp106/omrjs-core-ui/icons'
import { ProductLogo } from '@ramp106/omrjs-reviews-ui'
import { reviewsPaths } from '@ramp106/omrjs-routing'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useOmrEchartsTheme } from '@/composables/useOmrEchartsTheme'
import { baseOmrUrl } from '@/config/env'
import type { ProductById } from '@/gql/reviews'
import type {
  CompetitionVisibilityCaption,
  MarketChartsData,
  MarketHeroes,
  MarketInsightsView,
  MarketRankingData,
  MarketRatedTool,
  MarketTopTen,
  MarketTopTool,
} from '@/gql/reviews/mappers/insights'

const props = withDefaults(
  defineProps<{
    product: ProductById
    ranking: MarketRankingData
    heroes: MarketHeroes
    charts: MarketChartsData
    newEntrants: MarketRatedTool[]
    hiddenNewEntrants?: number
    topTen: MarketTopTool[]
    topTenState: MarketTopTen['state']
    states: MarketInsightsView['states']
    loading?: boolean
    productsLoading?: boolean
    topTenLoading?: boolean
  }>(),
  { hiddenNewEntrants: 0, loading: false, productsLoading: false, topTenLoading: false },
)

const PLACEHOLDER_LOGO = '/assets/images/placeholder-logo.svg'
const ENTRANTS_SINGLE_PAGE_MAX = 5
const ENTRANTS_PAGE_SIZE = 4
const TOP_TEN_PAGE_SIZE = 5

const { t, locale } = useI18n()
const { rootRef, tokens, ready } = useOmrEchartsTheme()

const hoveredWeek = ref<number | null>(null)

const entrantsPage = ref(0)
const entrantsPageSize = computed(() =>
  props.newEntrants.length <= ENTRANTS_SINGLE_PAGE_MAX ? ENTRANTS_SINGLE_PAGE_MAX : ENTRANTS_PAGE_SIZE,
)
const pageCount = computed(() => Math.ceil(props.newEntrants.length / entrantsPageSize.value))
const showEntrantsPagination = computed(
  () => !props.loading && !props.productsLoading && props.states.newEntrants === 'ready' && pageCount.value > 1,
)
const visibleEntrants = computed(() =>
  props.newEntrants.slice(entrantsPage.value * entrantsPageSize.value, (entrantsPage.value + 1) * entrantsPageSize.value),
)
const showHiddenEntrants = computed(() => props.hiddenNewEntrants > 0 && entrantsPage.value >= pageCount.value - 1)

watch(
  () => props.newEntrants,
  () => {
    entrantsPage.value = 0
  },
)

const topTenPage = ref(0)
const topTenPageCount = computed(() => Math.ceil(props.topTen.length / TOP_TEN_PAGE_SIZE))
const showTopTenPagination = computed(() => !props.topTenLoading && props.topTenState === 'ready' && topTenPageCount.value > 1)
const visibleTopTen = computed(() => props.topTen.slice(topTenPage.value * TOP_TEN_PAGE_SIZE, (topTenPage.value + 1) * TOP_TEN_PAGE_SIZE))

watch(
  () => props.topTen,
  () => {
    topTenPage.value = 0
  },
)

const windowCaption = (key: string, { span, compared, params }: CompetitionVisibilityCaption) =>
  t(`Reviews.product.insights.market.${key}.${span}${compared ? '' : 'NoComparison'}`, params)

const profileUrl = (slug: string): string => `${baseOmrUrl}${reviewsPaths.product({ locale: locale.value, slug })}`

watch(
  () => props.ranking,
  () => {
    hoveredWeek.value = null
  },
)

const rankingHero = computed(() => {
  const index = hoveredWeek.value
  const point = index == null ? undefined : props.ranking.points[index]
  if (index != null && point) {
    const week = props.ranking.weeks[index]
    return { ...point, caption: point.trend ? t('Reviews.product.insights.market.ranking.captionHover', { week }) : week }
  }
  const { current } = props.ranking
  return { ...current, caption: current.trend ? t('Reviews.product.insights.market.ranking.caption') : (props.ranking.weeks.at(-1) ?? '') }
})
</script>
